import { initReactI18next } from "react-i18next";

import OtaClient from "@crowdin/ota-client";
import i18n from "i18next";
import ChainedBackend from "i18next-chained-backend";
import HttpBackend from "i18next-http-backend";
import LocalStorageBackend from "i18next-localstorage-backend";

import { LANGUAGE_CODE } from "consts";

const DISTRIBUTION_HASH = process.env.REACT_APP_CROWDIN_DISTRIBUTION_HASH;
const REACT_APP_CROWDIN_DISTRIBUTION_MANIFEST = `https://distributions.crowdin.net/${DISTRIBUTION_HASH}`;
const client = new OtaClient(DISTRIBUTION_HASH);

function requestWithXmlHttpRequest(options, url, payload, callback) {
  if (payload && typeof payload === "object") {
    // if (!cache) payload._t = Date.now()
    // URL encoded form payload must be in querystring format
    // payload = addQueryString('', payload).slice(1)
    // console.log(payload)
  }

  try {
    const x = new XMLHttpRequest();
    x.open("GET", url, 1);
    if (!options.crossDomain) {
      x.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    }
    x.withCredentials = !!options.withCredentials;
    if (payload) {
      x.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
    }
    if (x.overrideMimeType) {
      x.overrideMimeType("application/json");
    }
    let h = options.customHeaders;
    h = typeof h === "function" ? h() : h;
    if (h) {
      for (const i in h) {
        x.setRequestHeader(i, h[i]);
      }
    }
    x.onreadystatechange = () => {
      // eslint-disable-next-line
      x.readyState > 3 &&
        callback(x.status >= 400 ? x.statusText : null, {
          status: x.status || 200,
          data: x.responseText,
        }); // in android webview loading a file is status status 0
    };
    x.send(payload);
  } catch (e) {
    // eslint-disable-next-line
    console && console.log(e);
  }
}

// Create a custom backend class for backup
class BackupHttpBackend extends HttpBackend {
  constructor(services, options = {}, allOptions = {}, coreOptions = {}) {
    super(
      services,
      {
        ...options,
      },
      allOptions,
      coreOptions
    );
  }
}

const formatLanguageCodeForCrowdin = (lngs) => {
  // Fix language casing for Crowdin OTA: i18next lowercases 'sk-SK' to 'sk-sk' by default,
  // but Crowdin requires proper casing ('sk-SK') in the URL path to load the correct files.
  const lng = Array.isArray(lngs) ? lngs[0] : lngs;
  return lng.replace(/([a-z]{2})-([a-z]{2})/, (match, lang, region) => `${lang}-${region.toUpperCase()}`);
};

async function getNamespaces() {
  const manifest = await client.manifest;
  // manifest.files is an array of all namespace files in our distribution
  const namespaces = manifest.files.map((file) =>
    // Strip folder + extension → e.g. "aboutUs.json" → "aboutUs"
    file.split("/").pop()?.replace(".json", "")
  );
  return [...new Set(namespaces)]; // remove duplicates
}

const getI18nConfig = (namespaces = []) => ({
  lng: LANGUAGE_CODE.GERMAN,
  backend: {
    // Please do not change the order of 'backends' below.
    backends: [
      LocalStorageBackend, // Caching settings
      HttpBackend, // 1. Try OTA
      BackupHttpBackend, // 2. Fallback to local
    ],
    backendOptions: [
      {
        // options for caching
        prefix: "i18next_res_",
        expirationTime: 1000, // 1 day
      },
      {
        // options for OTA
        loadPath: (lngs) => {
          const lng = formatLanguageCodeForCrowdin(lngs);
          // Use backup for Zulu language since it does not exist on crowdin
          if (lng === "zu") {
            return `${process.env.REACT_APP_TRANSLATIONS || ""}/translations/${lng}/{{ns}}.json`;
          }
          return `${REACT_APP_CROWDIN_DISTRIBUTION_MANIFEST}/content/development/public/translations/${lng}/{{ns}}.json`;
        },
      },
      {
        // options for Backup
        loadPath: (lngs) => {
          const lng = formatLanguageCodeForCrowdin(lngs);
          return `${process.env.REACT_APP_TRANSLATIONS || ""}/translations/${lng}/{{ns}}.json`;
        },
        request: requestWithXmlHttpRequest,
      },
    ],
  },
  lowerCaseLng: true,
  load: "all",
  fallbackLng: LANGUAGE_CODE.GERMAN,
  fallbackNS: "global",
  debug: false,
  defaultNS: "nav",
  ns: namespaces?.length > 0 ? namespaces : ["global", "nav"],
  interpolation: {
    escapeValue: false,
    formatSeparator: ",",
  },
  useSuspense: true,
});

// Initialize i18n with all name spaces
export async function initializeI18n() {
  let allNameSpaces = [];

  try {
    allNameSpaces = await getNamespaces();
  } catch (error) {
    console.error("Failed to load namespaces from Crowdin:", error); // eslint-disable-line
  }

  return i18n
    .use(ChainedBackend)
    .use(initReactI18next)
    .init(getI18nConfig(allNameSpaces), (_err) => {
      // eslint-disable-next-line no-console
      if (_err) return console.error(_err);
      // eslint-disable-next-line no-console
      if (!process.env.JEST_WORKER_ID) console.log("Loading translation has been completed.");
    });
}

await initializeI18n();
export default i18n;
